/* ============================================================================
   Smart Sofa 4.0 — alternate PDP behaviour.

   Deliberately additive. Everything to do with variants, pricing, the cart and the
   cross-product gallery swap is already handled by variant-selects.js /
   variant-radios.js / product-form.js; this file only covers what those don't:

     1. the desktop gallery pin (CSS sticky can't express it) and the touch swipe
        between gallery slides
     2. the sticky buy bar
     3. mirroring the live configuration into the pills, benefit strip and sticky bar
     4. forcing muted autoplay on the story videos
     5. the feature tabs, FAQ accordion and accessories carousel
     6. moving a cold landing onto the recommended model (Smart+)

   Loaded by sections/configurator-sofa-4.liquid; the story sections reuse it and
   simply no-op when their markup isn't on the page.
   ========================================================================= */

(function () {
  'use strict';

  // Six sections load this file defensively, and a <script src> tag runs the file once
  // per tag — so everything below used to execute six times, stacking six copies of
  // every listener. Harmless for the idempotent ones; fatal for anything that toggles.
  // The FAQ opened and closed itself once per copy and, on an even count, settled shut,
  // which read as "the accordion does nothing". The accessory arrows scrolled six times
  // as far. The guards inside this file can't catch that on their own: each execution
  // gets its own closure, so each has its own fresh set of them.
  if (window.__sofa4PdpInit) return;
  window.__sofa4PdpInit = true;

  var MOBILE_QUERY = '(max-width: 749px)';

  // init() re-runs on shopify:section:load to pick up re-rendered markup, which would
  // otherwise bind a second handler to every section that did NOT reload. Elements are
  // flagged as they are wired so each is only ever claimed once.
  function claim(element, key) {
    if (element.dataset[key]) return false;
    element.dataset[key] = '1';
    return true;
  }

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function one(selector, root) {
    return (root || document).querySelector(selector);
  }

  /* --- money -------------------------------------------------------------- */

  // Mirrors the theme's own use of Shopify.formatMoney, with a self-contained
  // fallback so a missing currency script degrades to a readable price rather than
  // an empty CTA.
  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (e) {
        /* fall through */
      }
    }
    var amount = (cents / 100).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    if (format && format.indexOf('{{amount_no_decimals}}') !== -1) {
      amount = Math.round(cents / 100)
        .toString()
        .replace(/\d(?=(\d{3})+$)/g, '$&,');
    }
    return '$' + amount;
  }

  // Signed money for the model cards' "+$x / −$x". Whole dollars drop the decimals,
  // matching the seat and shape deltas the section renders in Liquid.
  function formatDelta(cents, format) {
    var abs = Math.abs(cents);
    var deltaFormat = abs % 100 === 0 ? '${{amount_no_decimals}}' : '${{amount}}';
    return (cents < 0 ? '−' : '+') + formatMoney(abs, deltaFormat || format);
  }

  // "Save $3,000". Whole dollars drop the decimals, matching the Liquid's
  // money_without_trailing_zeros.
  function formatSaving(cents, format, saveLabel) {
    var abs = Math.abs(cents);
    return (saveLabel || 'Save') + ' ' + formatMoney(abs, abs % 100 === 0 ? '${{amount_no_decimals}}' : format);
  }

  // Every card that quotes an alternative: a cheaper one is money back, so it reads
  // "Save $3,000" rather than "−$3,000", while a costlier one keeps the "+$x" — the
  // saving is the part worth selling. Mirrors what the section renders in Liquid.
  function formatCardDelta(cents, format, saveLabel) {
    return cents >= 0 ? formatDelta(cents, format) : formatSaving(cents, format, saveLabel);
  }

  /* ======================================================================
     1. Gallery pin

     The gallery column scrolls with the page until all of it is visible, then
     holds while the taller buy box keeps scrolling, then releases. position:
     sticky can't express that (the element has to exit via the top), so the
     inner wrapper is translated instead.

     Which edge it holds by depends on whether it fits: a gallery shorter than the
     viewport holds by its top, a taller one by its bottom so its end is still
     reachable. Holding by the bottom unconditionally pushes a short gallery down
     the column on load, which is not the resting position the design shows.
     ====================================================================== */

  var pinState = { outer: null, inner: null, bound: false };

  var PIN_TOP_GAP = 16;
  var PIN_BOTTOM_GAP = 8;

  // <sticky-header> is only in the DOM when the header section's sticky setting is
  // on. When it is, the header slides away on the way down but comes back on the way
  // up, so the pin has to hold below it or it covers the config pill.
  function pinTopGap() {
    var header = document.querySelector('sticky-header');
    return header ? header.offsetHeight + PIN_TOP_GAP : PIN_TOP_GAP;
  }

  function initGalleryPin() {
    var outer = one('[data-s4-gallery-outer]');
    var inner = one('[data-s4-gallery-inner]');
    if (!outer || !inner) return;

    // Re-point at the current elements; the scroll/resize listeners below are bound
    // once and read through this state, so a section reload can't stack them.
    pinState.outer = outer;
    pinState.inner = inner;

    if (!pinState.bound) {
      pinState.bound = true;
      window.addEventListener('scroll', requestPin, { passive: true });
      window.addEventListener('resize', requestPin);
    }

    // Gallery swaps change the column height without firing scroll or resize.
    if (window.ResizeObserver) {
      if (pinState.observer) pinState.observer.disconnect();
      pinState.observer = new ResizeObserver(requestPin);
      pinState.observer.observe(inner);
    }

    updatePin();
  }

  function updatePin() {
    pinState.ticking = false;
    var outer = pinState.outer;
    var inner = pinState.inner;
    if (!outer || !inner || !outer.isConnected) return;

    if (isMobile()) {
      inner.style.transform = '';
      return;
    }

    var innerHeight = inner.offsetHeight;
    var outerHeight = outer.offsetHeight;

    // Nothing to pin while the column already fits.
    if (outerHeight <= innerHeight) {
      inner.style.transform = '';
      return;
    }

    // The outer column is never transformed, so its top is a stable reading — the
    // inner's own translate doesn't feed back into it (transforms don't affect layout).
    var outerTop = outer.getBoundingClientRect().top;
    var topGap = pinTopGap();
    var y;

    if (innerHeight <= window.innerHeight - topGap - PIN_BOTTOM_GAP) {
      // Fits on screen: hold it just under the header. While the column top is still
      // below that line — the whole of the first screen — this is negative and clamps
      // to 0, which is what keeps the gallery at its natural top on load.
      y = topGap - outerTop;
    } else {
      // Taller than the space it has: anchor the bottom instead, so the end of the
      // gallery is reachable rather than permanently past the fold.
      y = window.innerHeight - PIN_BOTTOM_GAP - (outerTop + innerHeight);
    }

    y = Math.max(0, Math.min(y, outerHeight - innerHeight));
    inner.style.transform = 'translateY(' + y + 'px)';
  }

  function requestPin() {
    if (pinState.ticking) return;
    pinState.ticking = true;
    window.requestAnimationFrame(updatePin);
  }

  /* ======================================================================
     1b. Thumbnail strip — drag to scroll, click to centre, arrows to page

     Bound by delegation on the document rather than to each strip: variant-radios.js
     injects a whole <media-gallery> per sibling config as the shopper explores, and
     those arrive long after this runs.
     ====================================================================== */

  var THUMB_LIST = '.sofa4-gallery__thumbs .thumbnail-list';
  var DRAG_THRESHOLD = 5;
  var thumbStripBound = false;
  var drag = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function closestIn(target, selector) {
    return target && target.closest ? target.closest(selector) : null;
  }

  function centreThumb(item) {
    var list = closestIn(item, THUMB_LIST);
    if (!list) return;

    // Measured rather than taken from offsetLeft: the items are position:relative for
    // the video badge, so their offsetParent is the stage, not the strip.
    var itemBox = item.getBoundingClientRect();
    var listBox = list.getBoundingClientRect();
    var delta = itemBox.left + itemBox.width / 2 - (listBox.left + listBox.width / 2);
    if (Math.abs(delta) < 1) return;

    // scrollTo clamps for us, so the first and last thumbnails simply travel as far as
    // the strip allows instead of refusing to move.
    list.scrollTo({
      left: list.scrollLeft + delta,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  function endThumbDrag() {
    if (!drag) return;
    var list = drag.list;
    var moved = drag.moved;

    list.classList.remove('is-dragging');
    if (drag.captured && list.releasePointerCapture) {
      try {
        list.releasePointerCapture(drag.pointerId);
      } catch (e) {
        /* pointer already gone */
      }
    }
    drag = null;

    if (!moved) return;

    // A drag that finishes over a thumbnail still fires that thumbnail's click, which
    // would swap the main image on what was only a scroll. Swallowed in the capture
    // phase so media-gallery.js never sees it, and torn down on the next tick in case
    // the release happened somewhere that produces no click at all.
    var swallow = function (event) {
      event.stopPropagation();
      event.preventDefault();
    };
    list.addEventListener('click', swallow, { capture: true, once: true });
    window.setTimeout(function () {
      list.removeEventListener('click', swallow, true);
    }, 0);
  }

  function initThumbStrip() {
    if (thumbStripBound) return;
    thumbStripBound = true;

    document.addEventListener('pointerdown', function (event) {
      // Touch and pen already pan the strip natively; hijacking those would only fight
      // the browser's own inertia. This is the mouse affordance.
      if (event.pointerType === 'touch' || event.button !== 0) return;
      var list = closestIn(event.target, THUMB_LIST);
      if (!list) return;
      drag = {
        list: list,
        startX: event.clientX,
        startScroll: list.scrollLeft,
        pointerId: event.pointerId,
        moved: false,
        captured: false,
      };
    });

    document.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.pointerId) return;
      var dx = event.clientX - drag.startX;

      if (!drag.moved) {
        // Below the threshold this is still a click, so leave it alone.
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.moved = true;
        drag.list.classList.add('is-dragging');
        if (drag.list.setPointerCapture) {
          try {
            drag.list.setPointerCapture(event.pointerId);
            drag.captured = true;
          } catch (e) {
            /* capture is an optimisation, not a requirement */
          }
        }
      }

      drag.list.scrollLeft = drag.startScroll - dx;
      event.preventDefault();
    });

    document.addEventListener('pointerup', endThumbDrag);
    document.addEventListener('pointercancel', endThumbDrag);

    // Bubble phase, so the capture-phase swallow above wins on a drag and this only
    // runs for a genuine click — which media-gallery.js has already acted on.
    document.addEventListener('click', function (event) {
      var item = closestIn(event.target, '.sofa4-gallery__thumbs .thumbnail-list__item');
      if (item) centreThumb(item);
    });
  }

  /* The arrow buttons either side of the strip. They page the strip's own scroll —
     they do not change the active image, which is what the thumbnails and the swipe
     are for.

     They used to be `.slider-button` / name="previous", which SliderComponent in
     global.js claims. It drives its arrows off the MAIN slide list, and that list is a
     one-at-a-time fader here, so only ever one slide measures as visible: update()
     read the first and last slide as both on screen and set `disabled` on both arrows
     on every render. Nothing was clickable. The markup no longer matches what
     SliderComponent looks for, and the buttons are driven from here instead. */

  var thumbArrowsBound = false;
  var thumbResizeObserver = null;
  var thumbStageObserver = null;

  function thumbWrapOf(node) {
    return closestIn(node, '.sofa4-gallery__thumbs');
  }

  function syncThumbArrows(wrap) {
    if (!wrap) return;
    var prev = one('[data-s4-thumb-prev]', wrap);
    var next = one('[data-s4-thumb-next]', wrap);

    // The arrows page the active image with wrap-around, so there is no edge to
    // fade at — they only disable when the gallery has a single reachable slide
    // and there is genuinely nowhere to go.
    var gallery = closestIn(wrap, 'media-gallery');
    var count =
      gallery && typeof gallery.navigableSlides === 'function' ? gallery.navigableSlides().length : 0;
    if (prev) prev.disabled = count < 2;
    if (next) next.disabled = count < 2;
  }

  function syncAllThumbArrows() {
    all('.sofa4-gallery__thumbs').forEach(syncThumbArrows);
  }

  function observeThumbStrips() {
    if (!thumbResizeObserver) return;
    // observe() on an already-observed target re-registers rather than duplicating, so
    // this is safe to run again for each newly injected gallery.
    all('.sofa4-gallery__thumbs .thumbnail-list').forEach(function (list) {
      thumbResizeObserver.observe(list);
    });
  }

  function initThumbArrows() {
    if (!thumbArrowsBound) {
      thumbArrowsBound = true;

      document.addEventListener('click', function (event) {
        var button = closestIn(event.target, '[data-s4-thumb-prev], [data-s4-thumb-next]');
        if (!button || button.disabled) return;
        // The arrows advance the ACTIVE IMAGE by one, not the strip's scroll —
        // paging thumbnails while the photo stood still read as broken (Ben,
        // Aug 22). Same navigation the swipe uses, so both land on the same
        // ordered set, including filtered variant galleries.
        var gallery = closestIn(button, 'media-gallery');
        if (!gallery || typeof gallery.nextSlide !== 'function') return;
        if (button.hasAttribute('data-s4-thumb-next')) {
          gallery.nextSlide();
        } else {
          gallery.previousSlide();
        }
        // Keep the now-active thumbnail in view; setActiveThumbnail only scrolls
        // the mobile dot strip, not this desktop list.
        var wrap = thumbWrapOf(button);
        var activeThumb = wrap ? one('.thumbnail-list__item [aria-current="true"]', wrap) : null;
        var thumbItem = activeThumb ? closestIn(activeThumb, '.thumbnail-list__item') : null;
        if (thumbItem) {
          thumbItem.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      });

      // Capture phase: scroll events don't bubble, so a listener on the document only
      // sees a strip's scroll on the way down. Covers the drag and the native pan as
      // well as the arrows' own smooth scroll.
      document.addEventListener(
        'scroll',
        function (event) {
          var target = event.target;
          if (!target || !target.classList || !target.classList.contains('thumbnail-list')) return;
          syncThumbArrows(thumbWrapOf(target));
        },
        { capture: true, passive: true },
      );

      // Covers both a window resize and the reveal of a config's gallery: a hidden one
      // measures 0 wide, so it reports a size change the moment it is shown.
      if (window.ResizeObserver) {
        thumbResizeObserver = new ResizeObserver(function (entries) {
          entries.forEach(function (entry) {
            syncThumbArrows(thumbWrapOf(entry.target));
          });
        });
      }
    }

    // variant-radios.js injects a gallery per sibling config as the shopper explores,
    // so the strips to watch are not all present when this first runs. Re-bound rather
    // than guarded, because a theme-editor reload replaces the stage element and the
    // previous observer would be left watching a detached node.
    var stage = one('.grid__item.product__media-wrapper');
    if (stage && window.MutationObserver) {
      if (thumbStageObserver) thumbStageObserver.disconnect();
      thumbStageObserver = new MutationObserver(function () {
        observeThumbStrips();
        syncAllThumbArrows();
      });
      thumbStageObserver.observe(stage, { childList: true });
    }

    observeThumbStrips();
    syncAllThumbArrows();
  }

  /* ======================================================================
     1c. Main image — swipe to change slide

     The gallery is a one-at-a-time fader, not a scrolling carousel: every slide but
     the active one is display:none (see .sofa4-gallery__list in sofa-4-pdp.css), so
     there is nothing for a finger to drag and the browser gives touch users no way
     to reach the next photo except the thumbnail strip. This reads the gesture and
     hands it to media-gallery.js's own nextSlide()/previousSlide(), which page
     through the same ordered set the thumbnails represent — so a swipe lands exactly
     where tapping the next thumbnail would, including on filtered variant galleries.

     Touch only, and only ever measured on touchend: nothing is preventDefault-ed
     during the gesture, so vertical scrolling over the image behaves as it always
     did. Bound by delegation for the same reason as the thumbnail strip — the
     galleries for sibling configs are injected later.
     ====================================================================== */

  var SWIPE_MIN = 40; // px of horizontal travel before it counts as a swipe
  var SWIPE_RATIO = 1.4; // and it has to beat the vertical travel by this much
  var SWIPE_CLICK_GUARD_MS = 350; // clicks arriving this soon after a swipe are the gesture's echo
  var gallerySwipeBound = false;
  var swipe = null;
  var lastSwipeAt = 0;

  function activeSlideId(gallery) {
    var active = one('.sofa4-gallery__list .product__media-item.is-active', gallery);
    return active ? active.getAttribute('data-media-id') : null;
  }

  // Directional cue for the incoming slide: without it the photo simply blinks and
  // the swipe reads as a glitch. Class is dropped once the animation is done so the
  // next change (thumbnail, variant swap) animates from a clean slate.
  function flagSwipeDirection(gallery, forward) {
    var active = one('.sofa4-gallery__list .product__media-item.is-active', gallery);
    if (!active) return;
    var className = forward ? 's4-swipe-next' : 's4-swipe-prev';
    active.classList.remove('s4-swipe-next', 's4-swipe-prev');
    // Reading offsetWidth between the remove and the add restarts the animation when
    // two swipes land on the same slide in a row.
    void active.offsetWidth;
    active.classList.add(className);
    window.setTimeout(function () {
      active.classList.remove(className);
    }, 400);
  }

  function initGallerySwipe() {
    if (gallerySwipeBound) return;
    gallerySwipeBound = true;

    document.addEventListener(
      'touchstart',
      function (event) {
        swipe = null;
        // Mobile swipes natively now — the list is a scroll-snap carousel there
        // and the browser owns the gesture. This JS gesture only serves the
        // desktop/tablet fader, where slides can't scroll.
        if (isMobile()) return;
        if (event.touches.length !== 1) return;

        // Started on the main image, not the thumbnail strip below it — that one
        // pans natively and must keep doing so.
        var list = closestIn(event.target, '.sofa4-gallery__list');
        if (!list) return;

        var gallery = closestIn(list, 'media-gallery');
        // Guarded rather than assumed: media-gallery.js is deferred like this file,
        // and a gallery whose element has not upgraded yet has no slide methods.
        if (!gallery || typeof gallery.nextSlide !== 'function') return;

        var touch = event.touches[0];
        swipe = { gallery: gallery, x: touch.clientX, y: touch.clientY, cancelled: false };
      },
      { passive: true }
    );

    // A second finger means a pinch-zoom on the photo, which ends with the fingers
    // far from where they started and would otherwise register as a swipe.
    document.addEventListener(
      'touchmove',
      function (event) {
        if (swipe && event.touches.length > 1) swipe.cancelled = true;
      },
      { passive: true }
    );

    document.addEventListener(
      'touchend',
      function (event) {
        var gesture = swipe;
        swipe = null;
        if (!gesture || gesture.cancelled || event.touches.length) return;

        var touch = event.changedTouches && event.changedTouches[0];
        if (!touch) return;

        var dx = touch.clientX - gesture.x;
        var dy = touch.clientY - gesture.y;
        // A scroll that drifts sideways is still a scroll.
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;

        // Suppress the click the browser synthesises after this touchend at the
        // source. The capture-phase swallow below raced it — its setTimeout(0)
        // removal could run first, letting the click through to the slide's
        // modal-opener, which opened the zoom lightbox mid-swipe and read as the
        // gallery "flipping through" images on its own.
        if (event.cancelable) event.preventDefault();

        var forward = dx < 0;
        var before = activeSlideId(gesture.gallery);
        if (forward) {
          gesture.gallery.nextSlide();
        } else {
          gesture.gallery.previousSlide();
        }
        // A gallery filtered down to one photo has nowhere to go, and animating the
        // slide it is already on would read as a broken bounce.
        if (activeSlideId(gesture.gallery) !== before) flagSwipeDirection(gesture.gallery, forward);

        // Arm the click guard below. The old approach — a once-only capture
        // swallow removed in setTimeout(0) — raced the browser's synthesised
        // click and lost on real devices, letting it through to the zoom
        // opener or a thumbnail, which read as the gallery snapping to another
        // image on its own.
        lastSwipeAt = Date.now();
      },
      { passive: false }
    );

    // Any click landing this soon after a handled swipe is the gesture's echo,
    // not an intention — the synthesised post-touchend click, wherever the
    // browser aims it. Timestamp-gated rather than added-and-removed per
    // gesture, so there is no listener lifecycle to race.
    document.addEventListener(
      'click',
      function (event) {
        if (Date.now() - lastSwipeAt < SWIPE_CLICK_GUARD_MS) {
          event.stopPropagation();
          event.preventDefault();
        }
      },
      { capture: true }
    );

    document.addEventListener('touchcancel', function () {
      swipe = null;
    });
  }

  /* ======================================================================
     1c-bis. Mobile carousel — track the snapped slide

     On mobile the gallery list scrolls natively (see the stylesheet); this
     watches the scroll settle and promotes the snapped slide to is-active via
     setActiveMedia, so the thumbnails, the sticky bar's photo mirror and the
     variant pipeline all follow a finger swipe exactly as they follow a
     thumbnail tap. Debounced rather than per-frame: setActiveMedia itself
     scrolls the list to the active item, and calling it mid-momentum would
     fight the gesture. After the snap it re-scrolls to where the list already
     rests, which is a no-op.
     ====================================================================== */

  var carouselScrollBound = false;
  var carouselSettleTimer = null;

  // Marks the slides the carousel shows: the same navigable set the arrows page
  // through (visible thumbnails), not the full media list — which carries every
  // colour's photos. Re-run from sync() so variant changes and product swaps
  // re-filter it.
  function syncCarouselSlides() {
    if (!isMobile()) return;
    all('media-gallery').forEach(function (gallery) {
      if (typeof gallery.navigableSlides !== 'function') return;
      var list = one('.sofa4-gallery__list', gallery);
      if (!list) return;
      var keep = {};
      gallery.navigableSlides().forEach(function (slide) {
        keep[slide.getAttribute('data-media-id')] = true;
      });
      all('.product__media-item', list).forEach(function (slide) {
        slide.classList.toggle('s4-in-carousel', keep[slide.getAttribute('data-media-id')] === true);
      });
    });
  }

  function initCarouselTracking() {
    syncCarouselSlides();
    if (carouselScrollBound) return;
    carouselScrollBound = true;
    window.addEventListener('resize', syncCarouselSlides);

    // Capture phase: scroll doesn't bubble, same trick as the thumbnail strip.
    document.addEventListener(
      'scroll',
      function (event) {
        if (!isMobile()) return;
        var list = event.target;
        if (!list || !list.classList || !list.classList.contains('sofa4-gallery__list')) return;

        window.clearTimeout(carouselSettleTimer);
        carouselSettleTimer = window.setTimeout(function () {
          var gallery = closestIn(list, 'media-gallery');
          if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

          var slides = all('.product__media-item', list).filter(function (slide) {
            return getComputedStyle(slide).display !== 'none';
          });
          if (!slides.length) return;
          var nearest = null;
          var nearestDistance = Infinity;
          slides.forEach(function (slide) {
            var distance = Math.abs(slide.offsetLeft - list.scrollLeft);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearest = slide;
            }
          });
          if (nearest && !nearest.classList.contains('is-active')) {
            gallery.setActiveMedia(nearest.getAttribute('data-media-id'), false, 'auto');
          }
        }, 140);
      },
      { capture: true, passive: true }
    );
  }

  /* ======================================================================
     1d. Owner photos — lightbox with click-to-zoom

     The customer photography in sofa4-trusted.liquid is shown at ~140px square, which
     is too small to see the thing being vouched for (the underseat storage, the light
     strip, the charging shelf). Each tile is a link to the full-size file; this
     intercepts it and opens the photo in a modal instead.

     Built on <dialog>: it gives the focus trap, the Esc key, the inert background and
     the return of focus to the tile for free, none of which are worth reimplementing.
     Where showModal isn't available the click is left alone and the link opens the
     photo, which is the whole reason the tile is a link.

     Zoom is done by resizing the image inside a scrollable stage rather than by
     transform: scale(). A scaled element doesn't produce scrollable overflow, so
     panning would have to be hand-rolled; at its natural size inside overflow:auto the
     browser pans it natively, with momentum on touch and a drag on desktop.
     ====================================================================== */

  var ZOOM_FACTOR = 2.5;
  var photoZoomBound = false;
  var zoomState = null;

  function buildZoomDialog() {
    var dialog = document.createElement('dialog');
    dialog.className = 'sofa4-zoom';
    dialog.setAttribute('aria-label', 'Customer photo');
    dialog.innerHTML =
      '<div class="sofa4-zoom__stage" data-s4-zoom-stage>' +
      '<img class="sofa4-zoom__img" data-s4-zoom-img alt="">' +
      '</div>' +
      '<button type="button" class="sofa4-zoom__close" data-s4-zoom-close aria-label="Close">&times;</button>' +
      '<button type="button" class="sofa4-zoom__nav sofa4-zoom__nav--prev" data-s4-zoom-prev aria-label="Previous photo">&#8249;</button>' +
      '<button type="button" class="sofa4-zoom__nav sofa4-zoom__nav--next" data-s4-zoom-next aria-label="Next photo">&#8250;</button>' +
      '<p class="sofa4-zoom__counter" data-s4-zoom-counter></p>';
    document.body.appendChild(dialog);
    return dialog;
  }

  function unzoomPhoto() {
    if (!zoomState) return;
    var img = zoomState.img;
    img.style.width = '';
    img.style.height = '';
    img.style.maxWidth = '';
    img.style.maxHeight = '';
    zoomState.stage.classList.remove('is-zoomed');
    zoomState.zoomed = false;
  }

  // Zooms about the point that was clicked, so the detail under the cursor is the
  // detail you end up looking at rather than the middle of the photo.
  function zoomPhotoAt(clientX, clientY) {
    if (!zoomState) return;
    var img = zoomState.img;
    var stage = zoomState.stage;
    var box = img.getBoundingClientRect();
    if (!box.width || !box.height) return;

    var relX = (clientX - box.left) / box.width;
    var relY = (clientY - box.top) / box.height;

    // Never past the file's own resolution — beyond that the shopper is being shown
    // blur, not detail.
    var natural = img.naturalWidth || 0;
    var target = box.width * ZOOM_FACTOR;
    if (natural > box.width) target = Math.min(target, natural);

    var scale = target / box.width;
    img.style.maxWidth = 'none';
    img.style.maxHeight = 'none';
    img.style.width = target + 'px';
    img.style.height = box.height * scale + 'px';
    stage.classList.add('is-zoomed');
    zoomState.zoomed = true;

    // Read back after the resize so the scroll extents are the new ones.
    stage.scrollLeft = relX * stage.scrollWidth - stage.clientWidth / 2;
    stage.scrollTop = relY * stage.scrollHeight - stage.clientHeight / 2;
  }

  function showPhoto(index) {
    if (!zoomState) return;
    var photos = zoomState.photos;
    if (!photos.length) return;

    var next = (index + photos.length) % photos.length;
    var link = photos[next];
    var thumb = link.querySelector('img');

    unzoomPhoto();
    zoomState.index = next;
    zoomState.img.setAttribute('src', link.getAttribute('href'));
    // The tile's alt describes the photo; the dialog shows the same photo.
    zoomState.img.setAttribute('alt', thumb ? thumb.getAttribute('alt') || '' : '');
    zoomState.stage.scrollTop = 0;
    zoomState.stage.scrollLeft = 0;

    var single = photos.length < 2;
    zoomState.counter.textContent = single ? '' : next + 1 + ' / ' + photos.length;
    zoomState.prev.hidden = single;
    zoomState.next.hidden = single;
  }

  function initPhotoZoom() {
    if (photoZoomBound) return;
    photoZoomBound = true;

    document.addEventListener('click', function (event) {
      var link = closestIn(event.target, '[data-s4-photo]');
      if (!link) return;
      // Modified clicks are the shopper asking for a new tab or a saved file; the
      // link is real, so let the browser honour them.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      var dialog = one('.sofa4-zoom') || buildZoomDialog();
      if (typeof dialog.showModal !== 'function') return; // link opens the photo instead

      event.preventDefault();

      if (!zoomState || zoomState.dialog !== dialog) {
        zoomState = {
          dialog: dialog,
          stage: one('[data-s4-zoom-stage]', dialog),
          img: one('[data-s4-zoom-img]', dialog),
          counter: one('[data-s4-zoom-counter]', dialog),
          prev: one('[data-s4-zoom-prev]', dialog),
          next: one('[data-s4-zoom-next]', dialog),
          photos: [],
          index: 0,
          zoomed: false,
        };

        one('[data-s4-zoom-close]', dialog).addEventListener('click', function () {
          dialog.close();
        });
        zoomState.prev.addEventListener('click', function () {
          showPhoto(zoomState.index - 1);
        });
        zoomState.next.addEventListener('click', function () {
          showPhoto(zoomState.index + 1);
        });

        // Click the photo to zoom, click again to come back out.
        zoomState.img.addEventListener('click', function (imgEvent) {
          if (zoomState.zoomed) {
            unzoomPhoto();
          } else {
            zoomPhotoAt(imgEvent.clientX, imgEvent.clientY);
          }
        });

        // Clicking the surround closes, the way a backdrop is expected to. The image
        // itself stops here, so a click that lands on it only ever zooms.
        zoomState.stage.addEventListener('click', function (stageEvent) {
          if (stageEvent.target === zoomState.stage) dialog.close();
        });

        dialog.addEventListener('keydown', function (keyEvent) {
          if (keyEvent.key === 'ArrowRight') showPhoto(zoomState.index + 1);
          if (keyEvent.key === 'ArrowLeft') showPhoto(zoomState.index - 1);
        });

        // Native close (Esc, or the button above) — put the page back.
        dialog.addEventListener('close', function () {
          unzoomPhoto();
          document.documentElement.classList.remove('s4-zoom-open');
        });
      }

      // Gathered per open rather than cached: the theme editor can add, remove or
      // reorder photo blocks between one open and the next.
      zoomState.photos = all('[data-s4-photo]', closestIn(link, '.sofa4-trusted__photos') || document);
      showPhoto(zoomState.photos.indexOf(link));

      document.documentElement.classList.add('s4-zoom-open');
      dialog.showModal();
    });
  }

  /* ======================================================================
     1e. Accessory colour circles

     An accessory is bought to match the sofa, so its colour is the sofa's colour. The
     circles on each card and the fabric/cupholder rows in the configurator are two
     views of one decision, and either can drive it: picking here moves the selection
     up, picking up there moves every card down here.

     Only the cards' own DOM changes on the way down (variant id, price, pressed state)
     — no events are dispatched — so the two directions can't chase each other.

     An accessory that doesn't stock the sofa's colour keeps whatever it had: a cushion
     that comes in three of the seven fabrics should stay addable, not silently switch
     to a colour the shopper didn't choose.
     ====================================================================== */

  var accSwatchesBound = false;

  function accMoneyFormat() {
    // The accessories section states its own format, including the currency code when
    // the theme setting asks for one; the configurator's is the fallback for a page
    // that has the cards without the buy box.
    var node = one('[data-s4-acc]') || one('[data-sofa4-root]');
    return (node && node.getAttribute('data-s4-money-format')) || '';
  }

  function selectAccSwatch(group, value) {
    var card = closestIn(group, '.sofa4-acc__card');
    var chosen = null;

    all('[data-s4-acc-swatch]', group).forEach(function (swatch) {
      var on = swatch.getAttribute('data-value') === value;
      swatch.classList.toggle('is-selected', on);
      swatch.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) chosen = swatch;
    });

    if (!chosen || !card) return;

    var variantId = chosen.getAttribute('data-variant-id');
    var input = one('[data-s4-acc-variant]', card);
    if (input && variantId) input.value = variantId;

    var priceNode = one('[data-s4-acc-price]', card);
    var price = parseFloat(chosen.getAttribute('data-price'));
    if (priceNode && !isNaN(price)) priceNode.textContent = formatMoney(price, accMoneyFormat());

    // The card's photo follows the chosen variant when that variant has its own
    // image; a swatch without one keeps whatever the card is already showing.
    // The Liquid's image_tag rendered a srcset for the merchandising image —
    // drop it, or the browser would keep picking the old candidates.
    var media = one('.sofa4-acc__media img', card);
    var image = chosen.getAttribute('data-image');
    if (media && image && media.getAttribute('src') !== image) {
      media.removeAttribute('srcset');
      media.removeAttribute('sizes');
      media.setAttribute('src', image);
    }

    // A colour can be sold out on its own. Leave the label alone — product-form.js and
    // the theme's strings own that — and only gate the submit.
    var button = one('[data-s4-acc-btn]', card);
    if (button) {
      if (chosen.getAttribute('data-available') === 'false') {
        button.setAttribute('disabled', '');
      } else {
        button.removeAttribute('disabled');
      }
    }
  }

  // Upward: the configurator's own radio is checked and told about it, so the whole
  // variant pipeline (price, gallery, availability) runs exactly as if the shopper had
  // clicked the swatch at the top of the page.
  function pushToConfigurator(kind, value) {
    var field = one('[data-s4-field="' + kind + '"]');
    if (!field) return;

    var radio = all('input[type="radio"]', field).filter(function (input) {
      return input.value === value;
    })[0];
    if (!radio || radio.checked || radio.disabled) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Downward, called from the mirror sync so it follows every configuration change.
  // The configurator's radios carry the bare option name while a 4.0 accessory's
  // values can carry a " 4.0" suffix — data-sync-value is the bare name the Liquid
  // emitted for exactly this comparison; data-value stays the accessory's own value.
  function syncAccSwatches(fabricName, cupName) {
    all('[data-s4-acc-swatches]').forEach(function (group) {
      var value = group.getAttribute('data-s4-acc-sync') === 'cup' ? cupName : fabricName;
      if (!value) return;

      var match = all('[data-s4-acc-swatch]', group).filter(function (swatch) {
        return (swatch.getAttribute('data-sync-value') || swatch.getAttribute('data-value')) === value;
      })[0];
      if (match) selectAccSwatch(group, match.getAttribute('data-value'));
    });
  }

  function initAccSwatches() {
    if (accSwatchesBound) return;
    accSwatchesBound = true;

    // Delegated: the cards are rendered by a different section, which may load after
    // this runs and can be re-rendered on its own in the theme editor.
    document.addEventListener('click', function (event) {
      var swatch = closestIn(event.target, '[data-s4-acc-swatch]');
      if (!swatch) return;

      var group = closestIn(swatch, '[data-s4-acc-swatches]');
      if (!group) return;

      // The circles sit inside the card's <form>; a stray submit would add to cart.
      event.preventDefault();

      var value = swatch.getAttribute('data-value');
      selectAccSwatch(group, value);
      // Push the bare name upward — the configurator's radios never carry an
      // accessory's " 4.0" suffix.
      pushToConfigurator(
        group.getAttribute('data-s4-acc-sync'),
        swatch.getAttribute('data-sync-value') || value
      );
    });
  }

  /* ======================================================================
     1b. Sticky top bar

     The configurator-phase counterpart of the bottom bar — the successor to
     the old configurator's .mobile-sticky-header. Shows while the shopper is
     inside the options (the gallery has scrolled off the top) and hides the
     moment the Add to cart is passed, where the bottom bar begins, so exactly
     one bar is on screen at a time. The stylesheet limits it to mobile: the
     desktop gallery pin keeps the sofa and price in view anyway.
     ====================================================================== */

  var topBarState = { bar: null, gallery: null, price: null, atc: null, bound: false };

  function initTopBar() {
    topBarState.bar = one('[data-s4-topbar]');
    topBarState.gallery = one('[data-s4-gallery-outer]');
    topBarState.price = one('.sofa4-price');
    topBarState.atc = one('[data-s4-atc]');
    if (!topBarState.bar || !topBarState.atc) return;
    if (!topBarState.gallery && !topBarState.price) return;

    if (!topBarState.bound) {
      topBarState.bound = true;
      window.addEventListener('scroll', updateTopBar, { passive: true });
      window.addEventListener('resize', updateTopBar);

      // Tap the photo to expand it to a full-width preview, tap again to
      // collapse. Delegated: the bar is re-rendered by the theme editor.
      document.addEventListener('click', function (event) {
        var photo = closestIn(event.target, '[data-s4-topbar-zoom]');
        if (!photo) return;
        var bar = closestIn(photo, '[data-s4-topbar]');
        if (bar) bar.classList.toggle('is-expanded');
        // The expanded bar is taller; whatever offsets against it follows.
        syncTopBarOffset();
      });
    }
    updateTopBar();
  }

  function updateTopBar() {
    var bar = topBarState.bar;
    if (!bar || !bar.isConnected) return;
    // The gallery leaving is the cue, because keeping the sofa on screen is what
    // this bar is for. It used to wait on `.sofa4-price`, which wraps the whole
    // price block — row, promo pill, reassurance hooks, financing line — so the
    // bar arrived roughly 450px of scroll later, once Build/Seats had already
    // pushed to the very top and the shopper was looking at Colour & fabric.
    // Falls back to the price if the gallery markup is ever absent.
    var anchor = topBarState.gallery || topBarState.price;
    var pastAnchor = anchor.getBoundingClientRect().bottom < 0;
    var beforeAtc = topBarState.atc.getBoundingClientRect().top > 0;
    var show = pastAnchor && beforeAtc;
    bar.classList.toggle('is-visible', show);
    // An expanded preview should not survive the bar leaving: sliding back in
    // later at full height would cover half the options.
    if (!show) bar.classList.remove('is-expanded');
    syncTopBarOffset();
  }

  // Anything else that drops from the viewport top — the theme's added-to-cart
  // notification — reads this flag and height to slide in BELOW the bar instead
  // of underneath it. Mobile-gated in JS because the stylesheet hides the bar on
  // desktop but the is-visible class is set at any width.
  function syncTopBarOffset() {
    var bar = topBarState.bar;
    var open = !!bar && bar.isConnected && isMobile() && bar.classList.contains('is-visible');
    document.documentElement.classList.toggle('s4-topbar-open', open);
    if (open) {
      document.documentElement.style.setProperty('--s4-topbar-height', bar.offsetHeight + 'px');
    }
  }

  /* ======================================================================
     1e. Chat bubble clearance

     Zipchat renders its launcher inside an open shadow root with inline
     position styles (bottom: 60px), which the sticky buy bar covers. Theme
     CSS can't pierce the shadow boundary, and the app's own offset setting is
     store-wide — so while the bar is on screen the bubble is lifted just
     above it, and restored the moment the bar goes. The original inline value
     is read once and kept, so a Zipchat-side setting change stays honoured.
     ====================================================================== */

  var chatBubbleRest = null;

  function liftChatBubble(visibleBar) {
    var host = document.getElementById('zipchat-shadow-host');
    var root = host && host.shadowRoot;
    if (!root) return;
    var bubble = root.querySelector('div[style*="position: fixed"]');
    if (!bubble) return;

    if (chatBubbleRest === null) chatBubbleRest = bubble.style.bottom || '60px';

    if (visibleBar) {
      var clearance = visibleBar.offsetHeight + 12;
      bubble.style.bottom = clearance + 'px';
    } else {
      bubble.style.bottom = chatBubbleRest;
    }
  }

  /* ======================================================================
     2. Sticky buy bar
     ====================================================================== */

  function initStickyBar() {
    var bar = one('[data-s4-sticky]');
    var atc = one('[data-s4-atc]');
    if (!bar || !atc) return;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          // Only when the CTA has left upward — scrolling *down* past it. If it is
          // still below the fold the shopper hasn't reached the buy box yet.
          var pastUpward = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', pastUpward);
          liftChatBubble(pastUpward ? bar : null);
        },
        { threshold: 0 }
      ).observe(atc);

      // The chat widget loads on its own schedule; if the bar is already up
      // when it arrives, the next toggle would be too late.
      window.setTimeout(function () {
        liftChatBubble(bar.classList.contains('is-visible') ? bar : null);
      }, 3000);
    }

    // One add-to-cart form on the page: the sticky button forwards to the real one.
    var stickyAtc = one('[data-s4-sticky-atc]', bar);
    if (stickyAtc) {
      stickyAtc.addEventListener('click', function () {
        atc.click();
      });
    }
  }

  /* ======================================================================
     3. Configuration mirroring

     Reads whichever radios are currently checked (they are the single source of
     truth — variant-radios.js writes them too) plus the price container the
     theme keeps up to date, and pushes the result into every [data-s4-mirror-*]
     node in the buy box, gallery pill and sticky bar.
     ====================================================================== */

  // The shape glyphs are drawn inset inside a 60-unit square, so the icon painted
  // about 14px right of the strip's left edge while the swatch row below it started
  // flush — the element was already aligned, the whitespace was inside the artwork.
  // Narrowing the viewBox to the glyph's own horizontal bounds pulls it left without
  // rescaling it: the y origin and height are kept, so units-per-pixel and the
  // baseline are untouched and only the dead space either side goes.
  function trimShapeIcon(node) {
    // Back to the stylesheet's 48px before measuring: this runs again on every sync
    // with a freshly cloned icon, and a height left over from the previous shape
    // would make each pass measure a smaller box than the last.
    node.style.height = '';
    var svg = node.firstElementChild;
    if (!svg || svg.tagName.toLowerCase() !== 'svg' || typeof svg.getBBox !== 'function') return;
    var view = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
    if (view.length !== 4 || view.some(isNaN)) return;
    var box;
    // getBBox throws in Firefox when the SVG is not being rendered — the sticky bar's
    // copy is display:none until it slides in. Leaving it untrimmed is the old look,
    // and the next sync() while it is visible corrects it.
    try {
      box = svg.getBBox();
    } catch (error) {
      return;
    }
    if (!box || !box.width || !box.height) return;

    // Scale the artwork is drawn at, in px per viewBox unit, read BEFORE anything is
    // changed so every icon keeps exactly the size it has today. The reset above is
    // what makes that safe on a re-sync: without it this would measure the previous
    // icon's fitted height and the glyphs would shrink a little on every change.
    var scale = svg.getBoundingClientRect().height / view[3];
    if (!scale) return;

    // Both axes. The glyphs are drawn inset inside a 60-unit square — the two-seater
    // occupies 12 units of the 60 vertically — so the box was ~38px taller than the
    // artwork and opened a gap between this row and the fabric row below it.
    svg.setAttribute('viewBox', box.x + ' ' + box.y + ' ' + box.width + ' ' + box.height);
    // Trimming the viewBox alone does nothing visible: the default xMidYMid re-centres
    // the content in whatever box the element occupies. xMin pins it to the left edge.
    svg.setAttribute('preserveAspectRatio', 'xMinYMid meet');
    svg.setAttribute('width', box.width);
    svg.setAttribute('height', box.height);
    // Explicit px at the original scale, rather than leaving the CSS height:100% to
    // re-fill the 48px box. Sizing the container too because its own height:48px
    // would otherwise keep the row tall no matter how small the glyph is.
    svg.style.width = scale * box.width + 'px';
    svg.style.height = scale * box.height + 'px';
    node.style.height = scale * box.height + 'px';
  }

  function initMirrors() {
    var root = one('[data-sofa4-root]');
    if (!root) return;

    var moneyFormat = root.getAttribute('data-s4-money-format') || '';
    var months = parseInt(root.getAttribute('data-s4-months'), 10) || 0;
    var atcLabel = root.getAttribute('data-s4-atc-label') || '';

    // See the note on data-s4-atc-label in the section: theme.liquid assigns
    // variantStrings after the section renders, so this is the earliest point at
    // which the design's CTA copy can be made to stick.
    if (atcLabel) {
      window.variantStrings = window.variantStrings || {};
      window.variantStrings.addToCart = atcLabel;
    }

    function checkedIn(field) {
      // The shape radios live in one <form> per seat count and only the visible
      // form's selection counts — the hidden ones keep their own checked input.
      if (field === 'shape') {
        return one('.sectionals form:not(.hidden) input[name="Sectionals"]:checked');
      }
      return one('[data-s4-field="' + field + '"] input[type="radio"]:checked');
    }

    function setText(name, value) {
      all('[data-s4-mirror-' + name + ']').forEach(function (node) {
        node.textContent = value == null ? '' : value;
      });
    }

    function setImage(name, url) {
      all('[data-s4-mirror-' + name + ']').forEach(function (node) {
        if (!url) {
          node.innerHTML = '';
          return;
        }
        var img = node.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.alt = '';
          img.loading = 'lazy';
          node.appendChild(img);
        }
        if (img.getAttribute('src') !== url) img.setAttribute('src', url);
      });
    }

    function sync() {
      var fabric = checkedIn('fabric');
      var cup = checkedIn('cup');
      var model = checkedIn('model');
      var seats = checkedIn('seats');
      var shape = checkedIn('shape');

      var fabricName = fabric ? fabric.value : '';
      var cupName = cup ? cup.value : '';
      var modelName = model ? model.value : '';
      var seatCount = seats ? seats.value : '';
      var shapeText = shape ? shape.getAttribute('data-sectionals-text') || '' : '';

      setText('fabric', fabricName);
      setText('cup', cupName);
      setText('model', modelName);

      if (fabric) setImage('swatch', fabric.getAttribute('data-swatch'));
      if (cup) setImage('cup-swatch', cup.getAttribute('data-swatch'));

      // The sticky bar shows the configured sofa itself: the gallery's active
      // slide already tracks every variant and cross-product swap, so mirror it
      // rather than re-deriving which image the configuration maps to.
      var activeGallery = one('media-gallery:not(.hidden)');
      var activeMedia = activeGallery ? one('.product__media-item.is-active img', activeGallery) : null;
      if (activeMedia) {
        setImage('variant-photo', activeMedia.currentSrc || activeMedia.getAttribute('src'));
      }

      // Storage callout: one line was rendered per configuration product (a
      // seat/shape change swaps products client-side, so a single build-time
      // number would go stale); show the one for the product on screen.
      if (activeGallery) {
        var storageProductId = activeGallery.getAttribute('data-product-id');
        all('[data-s4-storage]').forEach(function (line) {
          line.hidden = line.getAttribute('data-product-id') !== storageProductId;
        });
      }

      // Composition line beside the "Colour & fabric" heading.
      setText('fabric-props', fabric ? fabric.getAttribute('data-props') || '' : '');

      // The accessory cards further down the page carry the same two decisions.
      syncAccSwatches(fabricName, cupName);

      // Variant changes and product swaps re-filter which photos are reachable;
      // the mobile carousel has to show the same set.
      syncCarouselSlides();

      // The two option labels the theme's own configurator script would normally
      // maintain — that script isn't loaded here, so keep them in step directly.
      var selectedColor = one('#selected-color');
      if (selectedColor && fabricName) selectedColor.textContent = fabricName;
      var selectedCup = one('#selected-cupholder');
      if (selectedCup && cupName) selectedCup.textContent = cupName;

      // Smart+ gets the RGB gradient treatment.
      var isPlus = model ? model.getAttribute('data-plus') === 'true' : false;
      all('.sofa4-model-label').forEach(function (node) {
        node.classList.toggle('is-plus', isPlus);
      });

      syncStickyTitle(modelName, isPlus);

      // Shape icon: cloned from the selected card so it works for both the inline
      // SVG snippets and the metafield <img> fallback.
      var iconSource = shape ? one('[data-s4-shape-icon]', shape.nextElementSibling) : null;
      all('[data-s4-mirror-shape-icon]').forEach(function (node) {
        node.innerHTML = iconSource ? iconSource.innerHTML : '';
        trimShapeIcon(node);
      });

      // One wording for the whole page: every card that quotes a cheaper option frames
      // it as a saving, and the merchant sets that word once.
      var saveLabelNode = one('[data-s4-save-label]');
      var saveLabel = saveLabelNode ? saveLabelNode.getAttribute('data-s4-save-label') : 'Save';

      // Model cards show what switching would do to the total, not each model's own
      // total. Derived from the data-price that updateModelPrices() (variant-radios.js)
      // refreshes on every change for the live seat/shape/fabric combination, so the
      // baseline follows the rest of the configuration rather than the page load.
      syncModelDeltas(moneyFormat, saveLabel);

      // "Two things, for $300." — the figure is the gap between the cheapest and dearest
      // model for the configuration on screen, not a constant. Always the absolute
      // difference, so it reads as a price to pay whichever model is currently selected.
      var modelPrices = all('[data-s4-field="model"] input[type="radio"]')
        .map(modelCardPrice)
        .filter(function (price) {
          return price !== null;
        });
      if (modelPrices.length > 1) {
        var spread = Math.max.apply(null, modelPrices) - Math.min.apply(null, modelPrices);
        if (spread > 0) {
          setText('upgrade-price', formatMoney(spread, spread % 100 === 0 ? '${{amount_no_decimals}}' : moneyFormat));
        }
      }

      // Seat and shape cards re-quote off the selected shape's price, not the variant
      // data, so they stay right even before a config's variants have been fetched.
      var selectedLabelNode = one('[data-s4-selected-label]');
      syncCardDeltas(
        selectedLabelNode ? selectedLabelNode.getAttribute('data-s4-selected-label') : 'Selected',
        moneyFormat,
        saveLabel
      );

      // Config summary pill over the hero.
      var summary = [fabricName, seatCount ? seatCount + ' seats' : '', shapeText, modelName]
        .filter(Boolean)
        .join(' · ');
      setText('config', summary);

      // Restore the design's CTA copy after toggleAddButton() has run, but never
      // over a disabled button — that reads "Sold out" / "Unavailable".
      if (atcLabel) {
        var atcButton = one('[data-s4-atc]');
        if (atcButton && !atcButton.hasAttribute('disabled')) {
          var labelSpan = atcButton.querySelector('span');
          if (labelSpan && labelSpan.textContent.trim() !== atcLabel) {
            labelSpan.textContent = atcLabel;
          }
        }
      }

      // Price. data-price is the variant price; data-total-price is whatever the
      // theme's add-on logic has layered on top. Both are kept current by
      // variant-selects.js updatePriceDisplay().
      var container = one('.sofa4-price .price__container');
      if (container) {
        var total = parseFloat(container.getAttribute('data-price') || '0');
        total += parseFloat(container.getAttribute('data-total-price') || '0');
        if (!isNaN(total) && total > 0) {
          setText('price', formatMoney(total, moneyFormat));
          setText('cta-price', formatMoney(total, moneyFormat));

          // Struck compare price for the top bar. Only when it genuinely beats
          // the total — a compare equal to (or below) the price is not a deal.
          var compareTotal = parseFloat(container.getAttribute('data-compared-at') || '0');
          compareTotal += parseFloat(container.getAttribute('data-total-compare') || '0');
          setText('compare', compareTotal > total ? formatMoney(compareTotal, moneyFormat) : '');
          if (months > 0) {
            // Rounded to whole dollars, matching the design.
            var monthly = Math.round(total / months / 100) * 100;
            // Without the currency code: it already sits on the total right beside this,
            // and repeating it reads as "$244.00 CAD/mo". Derived from the shop's format
            // rather than hardcoded, so the symbol and grouping still come from Shopify.
            setText('monthly', formatMoney(monthly, moneyFormat.replace(/\s*[A-Z]{3}\s*$/, '')));
          }
          syncKlarna(total);
        }
      }

      syncRecap(container, moneyFormat, saveLabel);
    }

    var queued = false;
    function queueSync() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        sync();
      });
    }

    // A radio change kicks off the theme's variant pipeline; sync on the next frame
    // so the price container has already been rewritten.
    root.addEventListener('change', queueSync);

    // Add-ons and variant swaps can rewrite the price without a change event
    // reaching this root, so watch the container itself as well.
    var priceContainer = one('.sofa4-price .price__container');
    if (priceContainer && window.MutationObserver) {
      new MutationObserver(queueSync).observe(priceContainer, {
        attributes: true,
        attributeFilter: ['data-price', 'data-total-price', 'data-compared-at', 'data-total-compare'],
      });
    }

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, queueSync);
    }

    sync();
  }

  // Seat and shape cards. Liquid renders these deltas once, against the product the page
  // was served as, so after a swap the old card still read "Selected" and every quote was
  // measured from the wrong baseline. Re-quoted here off the data-s4-price each radio
  // carries — no variant lookup involved, so this holds for configs whose galleries and
  // variant data have not been fetched yet.
  function cardPrice(radio) {
    var price = parseFloat(radio.getAttribute('data-s4-price'));
    return isNaN(price) ? null : price;
  }

  function writeCardDelta(radio, text) {
    var node = radio.nextElementSibling ? one('.sofa4-card__delta', radio.nextElementSibling) : null;
    if (node) node.textContent = text;
  }

  // The sticky bar's title carries the model word — "Smart Sofa 4.0". Rather than
  // printing the model again as its own chip, the word itself becomes the selected
  // model, so Smart+ turns the title into "Smart+ Sofa 4.0" in the RGB treatment.
  // Rebuilt from data-s4-title-base every time, never from the current text, so a
  // second sync can't substitute into its own output.
  function syncStickyTitle(modelName, isPlus) {
    var node = one('[data-s4-sticky-title]');
    if (!node) return;

    var base = node.getAttribute('data-s4-title-base') || node.textContent;
    var match = modelName ? /smart\+?/i.exec(base) : null;

    // Everything around the model word is wrapped rather than left as bare text nodes:
    // the mobile bar shows the model on its own and needs something to hide.
    var rest = function (text) {
      var span = document.createElement('span');
      span.className = 'sofa4-sticky__title-rest';
      span.textContent = text;
      return span;
    };

    // A title that doesn't name a model is left exactly as the merchant wrote it.
    if (!match) {
      node.textContent = '';
      node.appendChild(rest(base));
      return;
    }

    var label = document.createElement('strong');
    label.className = 'sofa4-model-label' + (isPlus ? ' is-plus' : '');
    label.textContent = modelName;

    var before = base.slice(0, match.index);
    var after = base.slice(match.index + match[0].length);

    node.textContent = '';
    if (before) node.appendChild(rest(before));
    node.appendChild(label);
    if (after) node.appendChild(rest(after));
  }

  function syncCardDeltas(selectedLabel, moneyFormat, saveLabel) {
    // The current product is whichever shape is checked in the visible seat-count form,
    // the same rule getSelectedSectional() uses in variant-radios.js. Its price is the
    // baseline every card is quoted against, matching what the Liquid renders.
    var currentShape = one('.sectionals form:not(.hidden) input[name="Sectionals"]:checked');
    var baseline = currentShape ? cardPrice(currentShape) : null;
    if (baseline === null) return;

    var quote = function (radio) {
      if (radio.checked) {
        writeCardDelta(radio, selectedLabel);
        return;
      }
      var price = cardPrice(radio);
      // Seat counts with no product behind them carry no price and keep the blank the
      // section rendered, rather than being quoted against nothing.
      if (price !== null) writeCardDelta(radio, formatCardDelta(price - baseline, moneyFormat, saveLabel));
    };

    var seatField = one('[data-s4-field="seats"]');
    if (seatField) all('input[type="radio"]', seatField).forEach(quote);
    all('.sectionals form:not(.hidden) input[name="Sectionals"]').forEach(quote);
  }

  // Each model radio's label is its immediate nextElementSibling (the section keeps to
  // that contract for updateVariantAvailability), and the label holds the hidden
  // .price__container whose data-price is that model's total for the current config.
  function modelCardPrice(radio) {
    var container = radio.nextElementSibling ? one('.price__container', radio.nextElementSibling) : null;
    if (!container) return null;
    var price = parseFloat(container.getAttribute('data-price'));
    return isNaN(price) ? null : price;
  }

  function syncModelDeltas(moneyFormat, saveLabel) {
    var field = one('[data-s4-field="model"]');
    if (!field) return;

    var selectedLabel = field.getAttribute('data-s4-selected-label') || 'Selected';
    var radios = all('input[type="radio"]', field);
    var checked = radios.filter(function (radio) {
      return radio.checked;
    })[0];
    if (!checked) return;

    var basePrice = modelCardPrice(checked);
    if (basePrice === null) return;

    radios.forEach(function (radio) {
      var node = radio.nextElementSibling ? one('[data-s4-model-delta]', radio.nextElementSibling) : null;
      if (!node) return;

      if (radio === checked) {
        node.textContent = selectedLabel;
        node.classList.add('is-selected');
        return;
      }

      node.classList.remove('is-selected');
      var price = modelCardPrice(radio);
      // Leave whatever the server rendered rather than blanking the card if a model's
      // price can't be read — an empty slot reads as broken, a stale number does not.
      if (price !== null) node.textContent = formatCardDelta(price - basePrice, moneyFormat, saveLabel);
    });
  }

  // Price recap above the CTA: the struck original, the price being charged, and the
  // saving as a pill. Read from the same container the CTA total comes from, and
  // summed the same way variant-selects.js updatePriceDisplay() does — the add-on
  // logic keeps a matching pair (data-total-price / data-total-compare), so a
  // configuration with accessories layered on still strikes a like-for-like "was".
  //
  // Whether it is on sale at all is the configuration's business, not the page's: the
  // same sofa can carry a discount on one model and none on another, so the recap
  // hides and reappears with the selection rather than being decided once on load.
  function containerAmount(container, name) {
    var value = parseFloat(container.getAttribute(name) || '0');
    return isNaN(value) ? 0 : value;
  }

  function syncRecap(container, moneyFormat, saveLabel) {
    var recap = one('[data-s4-recap]');
    if (!recap) return;

    var price = container ? containerAmount(container, 'data-price') + containerAmount(container, 'data-total-price') : 0;
    var compare = container
      ? containerAmount(container, 'data-compared-at') + containerAmount(container, 'data-total-compare')
      : 0;
    var saving = compare - price;

    if (!(price > 0) || saving <= 0) {
      recap.hidden = true;
      return;
    }

    var now = one('[data-s4-recap-now]', recap);
    var was = one('[data-s4-recap-was]', recap);
    var save = one('[data-s4-recap-save]', recap);

    if (now) now.textContent = formatMoney(price, moneyFormat);
    // Only the amount is replaced: the <s> also carries a visually-hidden "Regular
    // price" for screen readers, which a textContent write would take out with it.
    if (was) {
      var label = one('.visually-hidden', was);
      was.textContent = '';
      if (label) was.appendChild(label);
      was.appendChild(document.createTextNode(formatMoney(compare, moneyFormat)));
    }
    if (save) save.textContent = formatSaving(saving, moneyFormat, saveLabel);

    recap.hidden = false;
  }

  /* ----------------------------------------------------------------------
     Default model

     Shopify serves the product's first available variant, which is Smart — so a
     shopper landing on the page cold started on the lower model even though Smart+
     is the one the page recommends. The selection is moved here rather than in
     Liquid because the price, gallery, variant input and CTA all hang off the
     served variant; flipping the radio alone would leave every one of them quoting
     Smart. Dispatching the change the way a click would runs the theme's whole
     variant pipeline (variant-radios.js), which also rewrites the URL with the
     Smart+ variant — that is what keeps this from re-firing later.

     Skipped whenever the URL already names a variant: a shared link, a back
     navigation or a restored session has made its own choice, and overriding it
     would silently upsell someone who deliberately picked Smart.
     ---------------------------------------------------------------------- */
  var defaultModelApplied = false;

  function initDefaultModel() {
    if (defaultModelApplied) return;

    var field = one('[data-s4-field="model"]');
    if (!field) return;

    var wanted = (field.getAttribute('data-s4-default-model') || '').trim().toLowerCase();
    if (!wanted) return;

    // Not a full URLSearchParams parse: `variant` has to be a parameter name, not a
    // substring of some other key or value.
    if (/[?&]variant=/.test(window.location.search)) {
      defaultModelApplied = true;
      return;
    }

    var target = all('input[type="radio"]', field).filter(function (radio) {
      return radio.value.trim().toLowerCase() === wanted;
    })[0];
    if (!target) return;

    defaultModelApplied = true;
    if (target.checked || target.disabled) return;

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ----------------------------------------------------------------------
     Klarna on-site messaging

     The app block is rendered once, for the product the page was served as, and its
     script is handed that product's variants and price. Seats and shape swap to a
     different product entirely, so there is nothing in that payload for Klarna to
     look the new price up from. Pushing the configured total onto the placement and
     asking Klarna to refresh keeps the quote honest — and it beats the variant price
     it was seeded with, since the total includes whatever add-ons are in play.
     ---------------------------------------------------------------------- */

  // Klarna's manager writes the attribute after we do, so a single set never sticks.
  // Bounded, because the correction is a fight with a third-party script: if a future
  // version of theirs reacts to our write by writing again, this stops rather than
  // ping-ponging forever. The cap is per target amount and per placement, so every real
  // config change gives each one a fresh budget.
  var KLARNA_MAX_CORRECTIONS = 8;
  var klarnaTarget = null;
  var klarnaEntries = [];

  function refreshKlarna() {
    // Klarna's library may not have landed yet; it reads the attribute on its own first
    // pass, so a missed refresh here is not a missed update.
    var osm = window.Klarna && window.Klarna.OnsiteMessaging;
    if (osm && typeof osm.refresh === 'function') osm.refresh();
  }

  function applyKlarnaAmount(placement, amount) {
    placement.setAttribute('data-purchase-amount', amount);
    refreshKlarna();
  }

  // One record per placement on the page, carrying its own correction budget and the
  // observer that spends it.
  function klarnaEntry(placement) {
    for (var i = 0; i < klarnaEntries.length; i++) {
      if (klarnaEntries[i].node === placement) return klarnaEntries[i];
    }

    var entry = { node: placement, corrections: 0 };
    klarnaEntries.push(entry);

    // Seats and shape swap to a different product, whose variants were never in the
    // payload the app block was seeded with. Klarna falls back to the price it started
    // with, so the quote has to be put back after every one of its writes.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!klarnaTarget || entry.corrections >= KLARNA_MAX_CORRECTIONS) return;
        if (placement.getAttribute('data-purchase-amount') === klarnaTarget) return;
        entry.corrections++;
        applyKlarnaAmount(placement, klarnaTarget);
      }).observe(placement, { attributes: true, attributeFilter: ['data-purchase-amount'] });
    }

    return entry;
  }

  // The app block renders one placement, up at the top of the buy box with the price.
  // The second sits with the recap by the CTA, where the decision is actually made.
  //
  // Cloned from the app's own element rather than written out in Liquid: the client id,
  // placement key, locale and theme are all the merchant's Klarna configuration, and a
  // hand-copied set in the theme would drift the day any of them changes. cloneNode
  // without children on purpose — attributes are the configuration, whereas the
  // children are whatever Klarna rendered into the original, which belongs to it.
  function ensureKlarnaClone(source) {
    var slot = one('[data-s4-klarna-clone]');
    if (!slot || slot.firstElementChild) return;

    var clone = source.cloneNode(false);
    clone.removeAttribute('id');
    slot.appendChild(clone);
    // Nothing renders in it until Klarna picks the new node up.
    refreshKlarna();
  }

  function syncKlarna(totalCents) {
    var source = one('.sofa4-buybox__app klarna-placement');
    if (source) ensureKlarnaClone(source);

    var placements = all('klarna-placement');
    if (!placements.length || !totalCents || totalCents <= 0) return;

    var amount = String(Math.round(totalCents));
    if (amount !== klarnaTarget) {
      klarnaTarget = amount;
      klarnaEntries.forEach(function (entry) {
        entry.corrections = 0;
      });
    }

    placements.forEach(function (placement) {
      klarnaEntry(placement);
      if (placement.getAttribute('data-purchase-amount') === amount) return;
      applyKlarnaAmount(placement, amount);
    });
  }

  /* ======================================================================
     4. Video autoplay

     Browsers block autoplay unless the element is muted, and the empty-string
     `muted` / `loop` attributes are not reliable across engines — the properties
     have to be set from script before play() is called.
     ====================================================================== */

  function initVideos(scope) {
    // Every video inside a .sofa4 root except the product gallery's, which is
    // click-to-play through Dawn's deferred-media and must not be hijacked.
    var videos = all('.sofa4 video', scope).filter(function (video) {
      return !video.closest('.sofa4-gallery__media') && !video.closest('deferred-media');
    });

    videos.forEach(function (video) {
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      var played = video.play();
      if (played && typeof played.catch === 'function') played.catch(function () {});
    });
  }

  /* ======================================================================
     5. Story-section interactions
     ====================================================================== */

  function initFeatureTabs() {
    all('[data-s4-tabs]').forEach(function (group) {
      if (!claim(group, "s4TabsBound")) return;
      var tabs = all('[data-s4-tab]', group);
      var clips = all('[data-s4-clip]', group);
      var captions = all('[data-s4-caption]', group);
      if (!tabs.length) return;

      function select(index) {
        tabs.forEach(function (tab, i) {
          tab.setAttribute('aria-selected', String(i === index));
        });
        clips.forEach(function (clip, i) {
          clip.hidden = i !== index;
        });
        captions.forEach(function (caption, i) {
          caption.hidden = i !== index;
        });
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(i);
        });
      });

      select(0);
    });
  }

  function initFaq() {
    all('[data-s4-faq]').forEach(function (list) {
      if (!claim(list, "s4FaqBound")) return;
      var items = all('[data-s4-faq-item]', list);

      items.forEach(function (item, index) {
        var toggle = one('[data-s4-faq-toggle]', item);
        var panel = one('[data-s4-faq-panel]', item);
        var sign = one('[data-s4-faq-sign]', item);
        if (!toggle || !panel) return;

        function setOpen(open) {
          panel.hidden = !open;
          toggle.setAttribute('aria-expanded', String(open));
          if (sign) sign.textContent = open ? '–' : '+';
        }

        toggle.addEventListener('click', function () {
          var willOpen = panel.hidden;
          // One at a time.
          items.forEach(function (other) {
            var otherPanel = one('[data-s4-faq-panel]', other);
            var otherToggle = one('[data-s4-faq-toggle]', other);
            var otherSign = one('[data-s4-faq-sign]', other);
            if (!otherPanel) return;
            otherPanel.hidden = true;
            if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
            if (otherSign) otherSign.textContent = '+';
          });
          if (willOpen) setOpen(true);
        });

        setOpen(index === 0);
      });
    });
  }

  function initAccessoriesCarousel() {
    all('[data-s4-acc]').forEach(function (section) {
      if (!claim(section, "s4AccBound")) return;
      var scroller = one('[data-s4-acc-scroller]', section);
      if (!scroller) return;

      function scrollBy(direction) {
        scroller.scrollBy({ left: direction * scroller.clientWidth * 0.85, behavior: 'smooth' });
      }

      var prev = one('[data-s4-acc-prev]', section);
      var next = one('[data-s4-acc-next]', section);
      if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
      if (next) next.addEventListener('click', function () { scrollBy(1); });
    });
  }

  // Guards the listeners bound to window/document rather than to section markup, so
  // a theme-editor section reload doesn't stack duplicates.
  var globalsBound = false;

  function initAnchorScroll() {
    if (globalsBound) return;
    globalsBound = true;

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-s4-scroll-to]');
      if (!trigger) return;
      var target = document.querySelector(trigger.getAttribute('data-s4-scroll-to'));
      if (!target) return;
      event.preventDefault();
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 70,
        behavior: 'smooth',
      });
    });
  }

  /* --- included-banner countdown ------------------------------------------ */

  // Same clock format as the top announcement-bar app. Two modes, mirroring it:
  //   fixed  — data-repeat-hours 0: counts to data-ends-at once, then hides the
  //            clock (or the whole banner, when the wrapper opts in).
  //   repeat — data-repeat-hours N: data-ends-at is an ANCHOR; the target rolls
  //            forward in N-hour periods from it, forever (the app's own math:
  //            336 h from its create date). Never expires, never hides.
  // The end time arrives as an ISO-8601 string with an explicit UTC offset,
  // which Date.parse treats identically in every browser. Anything unparseable
  // leaves the clock hidden — the banner simply has no countdown.
  function initIncludedCountdown() {
    all('[data-s4-countdown]').forEach(function (node) {
      // NOT 's4Countdown': claim() writes element.dataset[key], and that key maps
      // onto data-s4-countdown — the very attribute this node is selected by. It
      // happens to work while the attribute is valueless (dataset reads ""), but
      // it means the guard and the selector share one attribute, so giving the
      // marker a value in Liquid would silently stop the clock from ever binding.
      if (!claim(node, 's4CountdownBound')) return;
      var anchor = Date.parse(node.getAttribute('data-ends-at') || '');
      if (isNaN(anchor)) return;
      var repeatMs = (parseFloat(node.getAttribute('data-repeat-hours')) || 0) * 3600000;
      var wrap = node.closest('.sofa4-included');
      var autohide = wrap && wrap.hasAttribute('data-s4-countdown-autohide');
      var digits = {
        days: one('[data-s4-cd="days"]', node),
        hours: one('[data-s4-cd="hours"]', node),
        mins: one('[data-s4-cd="mins"]', node),
        secs: one('[data-s4-cd="secs"]', node),
      };
      if (!digits.days || !digits.hours || !digits.mins || !digits.secs) return;
      var timerId = null;

      function pad(n) {
        return n < 10 ? '0' + n : '' + n;
      }

      function target(now) {
        if (now < anchor || !repeatMs) return anchor;
        // Next rollover boundary strictly after now.
        var periods = Math.ceil((now - anchor) / repeatMs);
        var end = anchor + periods * repeatMs;
        return end > now ? end : end + repeatMs;
      }

      function render() {
        // Section re-renders in the theme editor replace this node; stop the
        // orphaned timer instead of ticking a detached element forever.
        if (!node.isConnected && timerId) {
          clearInterval(timerId);
          return;
        }
        var now = Date.now();
        var left = target(now) - now;
        if (left <= 0) {
          // Fixed mode only — repeat mode always has a future target.
          if (timerId) clearInterval(timerId);
          if (autohide && wrap) wrap.hidden = true;
          else node.hidden = true;
          return;
        }
        var s = Math.floor(left / 1000);
        digits.days.textContent = pad(Math.floor(s / 86400));
        digits.hours.textContent = pad(Math.floor((s % 86400) / 3600));
        digits.mins.textContent = pad(Math.floor((s % 3600) / 60));
        digits.secs.textContent = pad(s % 60);
        node.hidden = false;
      }

      render();
      if (repeatMs || anchor > Date.now()) timerId = setInterval(render, 1000);
    });
  }

  /* ---------------------------------------------------------------------- */

  function init() {
    initGalleryPin();
    initThumbStrip();
    initThumbArrows();
    initGallerySwipe();
    initPhotoZoom();
    initCarouselTracking();
    initAccSwatches();
    initTopBar();
    initStickyBar();
    initMirrors();
    // After initMirrors: the change it dispatches has to reach the listener that
    // syncs the pills, benefit strip and sticky bar.
    initDefaultModel();
    initVideos(document);
    initFeatureTabs();
    initFaq();
    initAccessoriesCarousel();
    initAnchorScroll();
    initIncludedCountdown();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor: sections are re-rendered in place, so re-bind against the new DOM.
  document.addEventListener('shopify:section:load', init);
})();
